import { keyframes } from '@emotion/react';
import { Box, Button, Group, Icon, OptimizeImage, Stack, Text } from '@inflearn/ds-react';
import { faRefresh } from '@inflearn/pro-regular-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { random } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { fetchClientApiV1AccountsMyProfile } from '../../../../../resources/@types/accountSchema';
import useClientAccountMyProfileUpdateMutation from '../../../../mutations/useClientAccountMyProfileUpdateMutation';
import useClientAccountProfileImageUploadMutation from '../../../../mutations/useClientAccountProfileImageUploadMutation';
import useClientAccountMyProfileQuery from '../../../../queries/useClientAccountMyProfileQuery';
import { getAccountProfileQueryKey } from '../../../../queries/useClientAccountProfileQuery';
import { GameStartScreen } from '../common/GameStartScreen';
import { CharacterPreview } from './CharacterPreview';
import { SelectionSparkle } from './SelectionSparkle';
import { SlotMachine } from './SlotMachine';
import { ZigzagLights } from './ZigzagLights';
import { blobToFile, composeCharacterImage } from './composeCharacterImage';
import { UI_IMAGES, getRandomItems, preloadImages } from './itemData';
import type { DressItem, SelectedItems } from './types';
import { CATEGORY_ORDER } from './types';

// 프레임 이미지 타입
type FrameImage = 'FRAME_1' | 'FRAME_2';

// 랜덤 프레임 선택 함수
const getRandomFrame = (): FrameImage => {
  return random() < 0.5 ? 'FRAME_1' : 'FRAME_2';
};

// 아웃트로 애니메이션
const slideInFromBottom = keyframes`
  0% {
    transform: translateY(100px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

interface SlotDressUpGameProps {
  isGameStarted: boolean;
  onGameStart: () => void;
  onGameOver: () => void;
  onImageSaved?: (cdnUrl: string) => void;
  onRestart?: () => void;
}

export const SlotDressUpGame = ({
  isGameStarted,
  onGameStart,
  onGameOver,
  onImageSaved,
  onRestart,
}: SlotDressUpGameProps) => {
  const queryClient = useQueryClient();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({
    expression: null,
    hat: null,
    top: null,
    background: null,
    effect: null,
  });
  const [slotItems, setSlotItems] = useState<DressItem[]>([]);
  const [isShowingResult, setIsShowingResult] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState<FrameImage>('FRAME_1');
  const [showSparkle, setShowSparkle] = useState(false);
  const [isProfileSaved, setIsProfileSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: uploadMutateAsync } = useClientAccountProfileImageUploadMutation();
  const { mutateAsync: profileUpdateMutateAsync } = useClientAccountMyProfileUpdateMutation({});
  const { data: profile } = useClientAccountMyProfileQuery();

  const currentCategory = CATEGORY_ORDER[currentStepIndex];
  const isLastStep = currentStepIndex === CATEGORY_ORDER.length - 1;

  // 컴포넌트 마운트 시 이미지 프리로드
  useEffect(() => {
    preloadImages();
  }, []);

  // 게임 시작
  const handleStart = useCallback(() => {
    // 첫 번째 카테고리의 랜덤 아이템 생성
    const firstCategory = CATEGORY_ORDER[0];
    const items = getRandomItems(firstCategory, 5);
    setSlotItems(items);
    setCurrentStepIndex(0);
    setSelectedItems({
      expression: null,
      hat: null,
      top: null,
      background: null,
      effect: null,
    });
    setIsShowingResult(false);
    onGameStart();
  }, [onGameStart]);

  // 아이템 선택 완료
  const handleItemSelected = useCallback(
    (item: DressItem) => {
      // 현재 선택을 포함한 새로운 selectedItems 생성
      const newSelectedItems = {
        ...selectedItems,
        [currentCategory]: item,
      };
      setSelectedItems(newSelectedItems);

      // 1초 후 자동으로 다음 단계로 이동
      setTimeout(() => {
        if (isLastStep) {
          // 마지막 단계면 랜덤 프레임 선택 후 결과 화면으로
          const frame = getRandomFrame();
          setSelectedFrame(frame);
          setIsShowingResult(true);
          // 스파클 효과 트리거
          setShowSparkle(true);
          setTimeout(() => setShowSparkle(false), 1000);

          // 게임 완료 (이미지 없이 결과 저장, 프로필 설정 시 CDN URL 업데이트)
          onGameOver();
        } else {
          // 다음 카테고리의 랜덤 아이템 생성
          const nextCategory = CATEGORY_ORDER[currentStepIndex + 1];
          const items = getRandomItems(nextCategory, 5);
          setSlotItems(items);
          setCurrentStepIndex((prev) => prev + 1);
        }
      }, 1000);
    },
    [currentCategory, isLastStep, currentStepIndex, selectedItems, onGameOver],
  );

  // 이미지 로컬 저장 (프레임 없이, 320px 너비)
  const handleDownloadImage = useCallback(async () => {
    try {
      // 프레임 제외, 320px 너비로 이미지 합성
      const blob = await composeCharacterImage(selectedItems, undefined, 320);

      // 다운로드 링크 생성
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `메리_${profile?.nickname}_상배.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert('이미지 다운로드에 실패했습니다. 다시 시도해 주세요.');
    }
  }, [selectedItems, profile?.nickname]);

  // 게임 재시작 (인트로로 돌아가기)
  const handleRestartGame = useCallback(() => {
    // 게임 내부 상태 초기화
    setCurrentStepIndex(0);
    setSelectedItems({
      expression: null,
      hat: null,
      top: null,
      background: null,
      effect: null,
    });
    setSlotItems([]);
    setIsShowingResult(false);
    setIsProfileSaved(false);
    setSelectedFrame('FRAME_1');
    setShowSparkle(false);
    setIsSaving(false);

    // 부모 컴포넌트에서 onRestart를 제공했다면 호출 (게임 상태를 false로 변경하여 인트로로 돌아감)
    if (onRestart) {
      onRestart();
    }
  }, [onRestart]);

  // 프로필 사진으로 설정
  const handleSetProfileImage = useCallback(async () => {
    setIsSaving(true);
    try {
      // 프레임 제외, 300x300 크기로 이미지 합성
      const blob = await composeCharacterImage(selectedItems, undefined, 300, 300);
      const file = blobToFile(blob, `character_${Date.now()}.png`);

      // 프로필 이미지 업로드
      const uploadResult = await uploadMutateAsync({ file });
      const fileId = uploadResult?.data?.fileId;
      const cdnUrl = uploadResult?.data?.cdnUrl;

      if (!fileId) {
        alert('이미지 업로드에 실패했습니다. 다시 시도해 주세요.');
        setIsSaving(false);
        return;
      }

      // 프로필 업데이트
      const { data: profileData } = await queryClient.ensureQueryData({
        queryKey: useClientAccountMyProfileQuery.queryKey,
        queryFn: () =>
          fetchClientApiV1AccountsMyProfile(useClientAccountMyProfileQuery.queryKey[0]),
      });

      await profileUpdateMutateAsync({
        profileImageFileId: fileId,
        nickname: profileData?.nickname ?? '',
        introduce: profileData?.introduce ?? '',
        slug: profileData?.slug ?? '',
      });

      queryClient.invalidateQueries({
        queryKey: getAccountProfileQueryKey(),
      });

      // 프로필 저장 완료 후 게임 결과 저장 및 모달 열기
      if (cdnUrl && onImageSaved) {
        onImageSaved(cdnUrl);
      }

      setIsProfileSaved(true);
    } catch {
      alert('이미지 업로드에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedItems, uploadMutateAsync, profileUpdateMutateAsync, queryClient, onImageSaved]);

  // 결과 화면 (isGameStarted보다 먼저 체크해야 결과 화면이 유지됨)
  if (isShowingResult) {
    const getProfileButtonText = () => {
      if (isSaving) return '저장 중...';
      if (isProfileSaved) return '설정 완료';
      return '프로필 사진으로 설정';
    };

    return (
      <Box
        pos="relative"
        mt={20}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '32px',
          height: '100%',
          padding: '40px',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <OptimizeImage
          src={UI_IMAGES.OUTRO.src}
          w={UI_IMAGES.OUTRO.w}
          alt="Slot Dress Up Outro"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        {/* 배경 반짝이 효과 */}
        <ZigzagLights />

        <Box
          pos="absolute"
          bottom={180}
          left={320}
          css={{
            zIndex: 1,
            animation: `${slideInFromBottom} 0.6s ease-out`,
          }}
        >
          <Box style={{ position: 'relative' }}>
            <CharacterPreview selectedItems={selectedItems} scale={1.5} hideBorder />
            <OptimizeImage
              src={UI_IMAGES[selectedFrame].src}
              w={UI_IMAGES[selectedFrame].w}
              alt="Character Frame"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${231 * 1.4}px`, // 323.4px
                height: `${222 * 1.4}px`, // 310.8px
                objectFit: 'contain',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            />
            {/* 캐릭터 주변 스파클 */}
            <SelectionSparkle isActive={showSparkle} />
          </Box>
        </Box>
        <Stack pos="absolute" bottom={43} left={0} right={0} spacing={8} align="center">
          <Button
            variant="filled"
            color="dark"
            onClick={handleRestartGame}
            leftIcon={<Icon icon={faRefresh} size="sm" />}
          >
            다시 준비하기
          </Button>
          <Group spacing={8} align="center">
            <Button
              w={180}
              variant="filled"
              color="infgreen"
              size="sm"
              onClick={handleDownloadImage}
            >
              이미지 저장
            </Button>
            <Button
              w={180}
              onClick={handleSetProfileImage}
              variant="filled"
              color="infgreen"
              size="sm"
              loading={isSaving}
              disabled={isProfileSaved}
            >
              {getProfileButtonText()}
            </Button>
          </Group>
        </Stack>
      </Box>
    );
  }

  // 게임 시작 전 화면
  if (!isGameStarted) {
    return (
      <GameStartScreen onGameStart={handleStart}>
        <Box
          pos="relative"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <OptimizeImage
            src={UI_IMAGES.INTRO.src}
            w={UI_IMAGES.INTRO.w}
            alt="Slot Dress Up Intro"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '50px',
            }}
          />
          <Box
            pos="absolute"
            bottom={0}
            left={0}
            w="100%"
            h="100%"
            css={{
              borderRadius: '40px',
              border: '16px solid white',
            }}
          ></Box>
        </Box>
      </GameStartScreen>
    );
  }

  // 슬롯 선택 진행 중
  return (
    <Stack
      w="100%"
      h={520}
      mt={20}
      style={{
        position: 'relative',
        display: 'flex',
        gap: '40px',
        padding: '20px',
        borderRadius: '20px',
        backgroundImage: `url(${UI_IMAGES.BG.src}?w=${UI_IMAGES.BG.w})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box
        style={{
          padding: '12px 24px',
        }}
      >
        <Text
          size="xl"
          weight={600}
          c="white"
          css={{
            fontFamily: 'Aggravo, sans-serif',
          }}
        >
          STEP {currentStepIndex + 1} / {CATEGORY_ORDER.length}
        </Text>
      </Box>
      <ZigzagLights />
      <Box pos="absolute" bottom={323} left={369}>
        <CharacterPreview selectedItems={selectedItems} />
      </Box>

      <SlotMachine
        key={`${currentCategory}-${currentStepIndex}`}
        items={slotItems}
        onItemSelected={handleItemSelected}
        stepIndex={currentStepIndex}
      />
    </Stack>
  );
};
